import React, { useEffect, useState } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { PageHeader, EmptyState } from '../../components/common/PageHeader';
import api from '../../lib/api';
import { Plus, Search, Edit2, Trash2, Users, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';

const emptyCustomer = { organization_name: '', contact_person: '', email: '', phone: '', street: '', house_number: '', postal_code: '', city: '', country: 'Deutschland', customer_type: 'privat', behoerden_billing_enabled: false, payment_on_invoice: false, notes: '' };

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCustomer);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const res = await api.get('/customers');
    setCustomers(res.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyCustomer); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        toast.success('Kunde aktualisiert');
      } else {
        await api.post('/customers', form);
        toast.success('Kunde angelegt');
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    } finally {
      setLoading(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm('Kunde wirklich löschen?')) return;
    await api.delete(`/customers/${id}`);
    toast.success('Gelöscht');
    load();
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/customers/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { imported, skipped, errors } = res.data;
      toast.success(`${imported} Kunden importiert${skipped ? `, ${skipped} übersprungen` : ''}`);
      if (errors?.length) {
        console.warn('CSV-Import-Fehler:', errors);
        toast.warning(`Erste Fehler: ${errors.slice(0, 3).join(' · ')}`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import fehlgeschlagen');
    } finally {
      e.target.value = '';
    }
  };

  const downloadTemplate = () => {
    const header = 'organization_name,contact_person,email,phone,street,house_number,postal_code,city,country,notes';
    const example = 'Musterkanzlei GmbH,Max Muster,max@muster.de,+49 30 1234567,Musterstraße,1,10115,Berlin,Deutschland,VIP-Kunde';
    const blob = new Blob([`${header}\n${example}\n`], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'kunden_vorlage.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  };

  const filtered = customers.filter(c =>
    !search ||
    c.organization_name.toLowerCase().includes(search.toLowerCase()) ||
    c.contact_person.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AppLayout>
      <PageHeader title="Kunden" description="Alle registrierten Kunden und Organisationen verwalten">
        <div className="flex gap-2 flex-wrap">
          <button onClick={downloadTemplate} data-testid="customer-csv-template-btn" className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-medium rounded-md px-3 py-2 text-sm">
            <Download className="w-4 h-4" /> CSV-Vorlage
          </button>
          <label className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-medium rounded-md px-4 py-2 text-sm cursor-pointer" data-testid="customer-csv-import-label">
            <Upload className="w-4 h-4" /> CSV-Import
            <input type="file" accept=".csv" onChange={handleCsvUpload} className="hidden" data-testid="customer-csv-import-input" />
          </label>
          <button onClick={openNew} data-testid="new-customer-btn" className="bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2 text-sm inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Neuer Kunde
          </button>
        </div>
      </PageHeader>

      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suche nach Firma, Ansprechpartner, E-Mail..."
          data-testid="customer-search"
          className="w-full border border-slate-300 rounded-md pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none"
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Users} title="Keine Kunden gefunden" description="Legen Sie einen neuen Kunden an, um zu beginnen." />
      ) : (
        <div className="bg-white border border-slate-200 rounded-md card-subtle overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Organisation</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Ansprechpartner</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">E-Mail</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Telefon</th>
                <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="border-b border-slate-100 py-3 px-4 text-sm font-medium text-slate-900">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>{c.organization_name}</span>
                      {c.behoerden_billing_enabled && (
                        <span data-testid={`badge-behoerden-${c.id}`} title="15-Min-Pauschale freigeschaltet" className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-1.5 py-0.5">
                          15-Min
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">{c.contact_person}</td>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">{c.email}</td>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">{c.phone || '-'}</td>
                  <td className="border-b border-slate-100 py-3 px-4 text-right">
                    <button onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`} className="inline-flex items-center gap-1 text-slate-600 hover:text-[#0F4C81] mr-3">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => del(c.id)} data-testid={`delete-customer-${c.id}`} className="inline-flex items-center gap-1 text-slate-600 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">{editing ? 'Kunde bearbeiten' : 'Neuer Kunde'}</h3>
            <form onSubmit={save} className="space-y-3" data-testid="customer-form">
              {[
                ['organization_name', 'Organisation / Firma / Behörde *', true],
                ['contact_person', 'Ansprechpartner *', true],
                ['email', 'E-Mail *', true, 'email'],
                ['phone', 'Telefon', false],
              ].map(([k, l, req, type]) => (
                <div key={k}>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">{l}</label>
                  <input
                    required={req}
                    type={type || 'text'}
                    value={form[k]}
                    onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))}
                    data-testid={`customer-field-${k}`}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none"
                  />
                </div>
              ))}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Adresse (für Rechnung)</label>
                <div className="grid grid-cols-[1fr_90px] gap-2 mb-2">
                  <input value={form.street || ''} onChange={(e) => setForm(f => ({ ...f, street: e.target.value }))} placeholder="Straße" data-testid="customer-field-street" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                  <input value={form.house_number || ''} onChange={(e) => setForm(f => ({ ...f, house_number: e.target.value }))} placeholder="Hausnr." data-testid="customer-field-housenr" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
                <div className="grid grid-cols-[110px_1fr] gap-2">
                  <input value={form.postal_code || ''} onChange={(e) => setForm(f => ({ ...f, postal_code: e.target.value }))} placeholder="PLZ" data-testid="customer-field-plz" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                  <input value={form.city || ''} onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Ort" data-testid="customer-field-city" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Kundentyp</label>
                <select
                  value={form.customer_type || 'privat'}
                  onChange={(e) => setForm(f => ({ ...f, customer_type: e.target.value }))}
                  data-testid="customer-field-type"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none"
                >
                  <option value="privat">Privat / Unternehmen</option>
                  <option value="behoerde">Behörde</option>
                  <option value="justiz">Justiz</option>
                </select>
              </div>
              <div className="border border-indigo-200 bg-indigo-50/50 rounded-md p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.behoerden_billing_enabled}
                    onChange={(e) => setForm(f => ({ ...f, behoerden_billing_enabled: e.target.checked }))}
                    data-testid="customer-field-behoerden-billing"
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#0F4C81] focus:ring-[#0F4C81]"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-slate-900">15-Min-Pauschale freischalten (Behörden-Abrechnung)</span>
                    <span className="block text-xs text-slate-600 mt-0.5">
                      Nur für freigeschaltete Behörden / Justiz. Aktiviert die Auswahl „15-Min-Pauschale + minutengenau" in der Buchungsmaske dieses Kunden.
                    </span>
                  </span>
                </label>
              </div>
              <div className="border border-amber-200 bg-amber-50/50 rounded-md p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.payment_on_invoice}
                    onChange={(e) => setForm(f => ({ ...f, payment_on_invoice: e.target.checked }))}
                    data-testid="customer-field-payment-invoice"
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#0F4C81] focus:ring-[#0F4C81]"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-slate-900">Zahlung auf Rechnung (kein Stripe Checkout)</span>
                    <span className="block text-xs text-slate-600 mt-0.5">
                      Standard ist Sofort-Bezahlung bei Buchung via Stripe (Karte / PayPal / Klarna). Aktivieren Sie diese Option für Stammkunden, die per Rechnung zahlen dürfen.
                    </span>
                  </span>
                </label>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Notizen</label>
                <textarea rows="3" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="customer-field-notes" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div className="flex gap-2 pt-2 justify-end">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Abbrechen</button>
                <button type="submit" disabled={loading} data-testid="customer-save-btn" className="bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2 text-sm disabled:opacity-50">
                  {loading ? 'Speichern...' : 'Speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default AdminCustomers;
